import React from 'react';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, Table, Button, Container } from 'react-bootstrap';
import Moment from 'react-moment';
import { observer, inject } from 'mobx-react';
import { Client } from '../../models';
import { ClientEntryStore } from '../../stores/client-entry-store';
import { BloodworkEntryStore } from '../../stores/bloodwork-entry-store';
import { UserProfile } from '../../models/UserProfile';
import { GenericModal } from '../common/generic-modal';
import { MathUtils } from '../../utils/MathUtils';

type SortDirection = 'asc' | 'desc';

// Sort by the underlying value (dates/numbers), not the formatted display string.
// Missing values sort to the start in asc / end in desc.
const SORT_ACCESSORS: { [field: string]: (c: Client) => string | number } = {
  lastName: (c) => (c.lastName || '').toLowerCase(),
  firstName: (c) => (c.firstName || '').toLowerCase(),
  gender: (c) => c.displayGender || '',
  dateOfBirth: (c) => c.dateOfBirth || '',
  age: (c) => c.age ?? Number.NEGATIVE_INFINITY,
  ethnicity: (c) => c.displayEthnicity || '',
  menopause: (c) => c.displayMenopause || '',
  trimester: (c) => c.trimester ?? Number.NEGATIVE_INFINITY,
  elevation: (c) => c.elevation ?? Number.NEGATIVE_INFINITY,
  height: (c) => c.height ?? Number.NEGATIVE_INFINITY,
  weight: (c) => c.weight ?? Number.NEGATIVE_INFINITY,
  bmi: (c) =>
    c.height != null && c.weight != null
      ? MathUtils.bmi(c.weight, c.height)
      : Number.NEGATIVE_INFINITY,
  cigaretteSmoker: (c) => (c.cigaretteSmoker ? 1 : 0),
  alcoholDrinker: (c) => (c.alcoholDrinker ? 1 : 0),
  createDate: (c) => c.createDate || '',
};

type Props = {
  clients: Client[];
  currentUserProfile: UserProfile;
  clientEntryStore: ClientEntryStore;
  bloodworkEntryStore: BloodworkEntryStore;
  history?: any;
};

type State = {
  showUpgradeModal: boolean;
  sortField: string;
  sortDirection: SortDirection;
};

@inject('clientEntryStore', 'bloodworkEntryStore')
@observer
export class ClientDisplay extends React.Component<Props, State> {
  state: State = {
    showUpgradeModal: false,
    // Default to newest-first so a just-added client stays at the top
    sortField: 'createDate',
    sortDirection: 'desc'
  };

  private savedRowRef = React.createRef<HTMLTableRowElement>();
  private lastScrolledSavedId?: string;
  private scrollTimer?: number;

  componentDidUpdate(): void {
    // After a save, scroll the just-saved client's row into view so the
    // practitioner can immediately click its "Add Test Results" button.
    const savedId = this.props.clientEntryStore.recentlySavedClientId;
    if (!savedId) {
      this.lastScrolledSavedId = undefined;
      return;
    }
    if (savedId === this.lastScrolledSavedId) {
      return;
    }
    this.lastScrolledSavedId = savedId;
    // Defer the scroll: saving also collapses the add/edit form and shows a
    // success alert, which shift the table's layout. Scrolling immediately would
    // measure the old position and leave the row off-screen, so wait for the
    // dust to settle first.
    window.clearTimeout(this.scrollTimer);
    this.scrollTimer = window.setTimeout(() => {
      if (this.savedRowRef.current) {
        this.savedRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 150);
  }

  componentWillUnmount(): void {
    window.clearTimeout(this.scrollTimer);
  }

  handleSort(field: string): void {
    // The practitioner is taking over ordering — stop pinning the saved client
    this.props.clientEntryStore.clearRecentlySavedClient();
    this.setState((prev) => {
      if (prev.sortField === field) {
        return { ...prev, sortDirection: prev.sortDirection === 'asc' ? 'desc' : 'asc' };
      }
      // Dates default to newest-first; everything else to A→Z / low→high
      const isDate = field === 'createDate' || field === 'dateOfBirth';
      return { ...prev, sortField: field, sortDirection: isDate ? 'desc' : 'asc' };
    });
  }

  renderSortIndicator(field: string): JSX.Element {
    const active = this.state.sortField === field;
    const arrow = !active ? '↕' : this.state.sortDirection === 'asc' ? '▲' : '▼';
    return (
      <span style={{ marginLeft: 4, fontSize: '0.75em', opacity: active ? 1 : 0.35 }}>{arrow}</span>
    );
  }

  sortableHeader(label: React.ReactNode, field: string): JSX.Element {
    return (
      <th
        style={{ verticalAlign: 'bottom', textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
        onClick={() => this.handleSort(field)}
        title="Click to sort"
      >
        {label}{this.renderSortIndicator(field)}
      </th>
    );
  }

  addTestResult(client: Client): void {
    const { clientEntryStore, bloodworkEntryStore, history } = this.props;
    clientEntryStore.clearRecentlySavedClient();
    clientEntryStore.setCurrentClient(client);
    bloodworkEntryStore.resetCurrentBloodwork();
    bloodworkEntryStore.setHideEnterTestResults(false);
    history.push(`/bloodwork/edit/${client.id}`);
  }

  deleteClient(client: Client): void {
    const deleteClient = window.confirm('Deleting a client will also delete all the test results for that client.\n\n' + 
      'Are you sure you want to delete this client and their test results?');
    
    if (deleteClient) {
      const { clientEntryStore, bloodworkEntryStore } = this.props;
      bloodworkEntryStore.loadClientBloodworks(client.id).then(() => {
        return bloodworkEntryStore.deleteClientBloodworks(client.id);        
      }).then(() => {
        if (bloodworkEntryStore.clientBloodworks.length === 0) {
          return Promise.resolve();
        } else {  
          return Promise.reject('Not all of the client bloodworks were deleted. Not deleting client.');
        }
      }).then(() => {
        return clientEntryStore.deleteClient(client);
      }).then(() => {
        return clientEntryStore.resetCurrentClient();
      }).catch((error) => {
        console.error(error);
        alert('There was an error trying to delete the client and their test results.');
      });
    }
  }

  editClient(client: Client) {
    const { currentUserProfile, clientEntryStore, history } = this.props;
    if (currentUserProfile.editClientAllowed) {
      // Clear any prior highlight; the next save re-surfaces the edited client
      clientEntryStore.clearRecentlySavedClient();
      if (history && history.location && history.location.pathname !== '/client/edit/manage') {
        history.push('/client/edit/manage');
      }
      clientEntryStore.setCurrentClient(client);
      clientEntryStore.setExpandAddClient(true);
      // This is a hack to expand add/edit client panel on first click of edit
      setTimeout(()=>{
        clientEntryStore.setExpandAddClient(true);
        this.scrollTop();
      }, 1000);
    } else {
      this.setState({
        showUpgradeModal: true
      });
    }
  }

  scrollTop() {
    document.body.scrollTop = 0; 
    document.documentElement.scrollTop = 0;
  }

  render() {
    const { clients, clientEntryStore } = this.props;
    const { showUpgradeModal } = this.state;

    if (!clients || !clients.length) {
      return null;
    }

    const savedId = clientEntryStore.recentlySavedClientId;
    let sortedClients = _.orderBy(
      clients,
      [SORT_ACCESSORS[this.state.sortField] || SORT_ACCESSORS.createDate],
      [this.state.sortDirection]
    );
    // Surface the just-added/edited client at the top, regardless of sort, so
    // its "Add Test Results" button is immediately reachable.
    if (savedId) {
      const idx = sortedClients.findIndex((c: Client) => c.id === savedId);
      if (idx > 0) {
        sortedClients = [
          sortedClients[idx],
          ...sortedClients.slice(0, idx),
          ...sortedClients.slice(idx + 1)
        ];
      }
    }

    return (
      <>
        <Container fluid>
          <Card>
            <Card.Header>Clients</Card.Header>
            <Card.Body>
              <div>
                { (clients.length > 0) ?
                  <Table striped bordered hover className="labsmartz-table">
                    <thead>
                      <tr>
                        <th style={{ verticalAlign: 'bottom', textAlign: 'center' }}>Actions</th>
                        {this.sortableHeader('Last Name', 'lastName')}
                        {this.sortableHeader('First Name', 'firstName')}
                        {this.sortableHeader('Sex', 'gender')}
                        {this.sortableHeader('Date of Birth', 'dateOfBirth')}
                        {this.sortableHeader('Age', 'age')}
                        {this.sortableHeader('Ethnicity', 'ethnicity')}
                        {this.sortableHeader(<>Menopause/<br/>Pregnant</>, 'menopause')}
                        {this.sortableHeader('Trimester', 'trimester')}
                        {this.sortableHeader('Elevation', 'elevation')}
                        {this.sortableHeader('Height', 'height')}
                        {this.sortableHeader('Weight', 'weight')}
                        {this.sortableHeader('BMI', 'bmi')}
                        {this.sortableHeader(<>Inhaled<br/>Smoke</>, 'cigaretteSmoker')}
                        {this.sortableHeader(<>Alcohol<br/>Drinker</>, 'alcoholDrinker')}
                        {this.sortableHeader('Date Created', 'createDate')}
                      </tr>
                    </thead>
                    <tbody>
                      {
                        sortedClients.map((client: Client, index: number) => {
                          const isRecentlySaved = client.id != null && client.id === savedId;
                          return (
                            <tr
                              key={client.id || index}
                              ref={isRecentlySaved ? this.savedRowRef : undefined}
                              className={isRecentlySaved ? 'client-row-recent' : undefined}
                            >
                              <td style={{ width: '150px' }}>
                                <Button 
                                  type="button"
                                  title="Add Test Results"
                                  onClick={() => { this.addTestResult(client); }}
                                >
                                  <FontAwesomeIcon icon="tint" />
                                </Button>
                                &nbsp;&nbsp;
                                <Button 
                                  type="button"
                                  title="Edit Client"
                                  onClick={() => { this.editClient(client); }}
                                >
                                  <FontAwesomeIcon icon="user-edit" />
                                </Button>
                                &nbsp;&nbsp;
                                <Button
                                  title="Delete Client"
                                  onClick={() => { this.deleteClient(client); }}
                                ><FontAwesomeIcon icon="trash-alt" />
                                </Button>                            
                              </td> 
                              <td>{client.lastName}</td>                          
                              <td>{client.firstName}</td>
                              <td>{client.displayGender}</td>
                              <td><Moment format="MM/DD/YYYY">{client.dateOfBirth}</Moment></td>
                              <td>{client.displayAge}</td>
                              <td>{client.displayEthnicity}</td>
                              <td>{client.displayMenopause}</td>
                              <td>{client.displayTrimester}</td>
                              <td>{client.displayElevation}</td>
                              <td>{client.displayHeight}</td>
                              <td>{client.displayWeight}</td>
                              <td>{client.displayBMI}</td>
                              <td>{client.displayInhaledSmoke}</td>
                              <td>{client.displayAlcoholDrinker}</td>
                              <td><Moment format="MM/DD/YYYY">{client.createDate}</Moment></td>
                            </tr>
                          );
                        })
                      }
                    </tbody>
                  </Table> : null
                }
              </div>
            </Card.Body>
          </Card>       
        </Container>
        <GenericModal 
          show={showUpgradeModal} 
          title="Edit Client Feature Not Available"
          text="Edit Client feature is not available with a free account."
          link="https://labsmarts.com/pricing"
          linkText="Click here to upgrade."
          onHide={() => { this.setState( { showUpgradeModal: false }); }}
        />
      </>
    );
  }
}
